import React, { useState, useEffect, useContext } from 'react';
import * as Dialog from "@radix-ui/react-dialog";
import { X, Upload } from 'lucide-react';
import SelectDropdown from '../components/SelectDropdown';
import { SettingsContext } from '../frames/Main';
import { isEmpty } from '../lib/Helper';
import { Project } from '../models/Project';
import { Module } from '../models/Module';
import { Slide } from '../models/Slide';
import { useToast } from '../components/ToastContext';
import { useDropzone } from 'react-dropzone';
import { getFileListFromFiles } from '../lib/Helper';

type SlideEditorProps = {
    trigger: React.ReactNode;
    projects: Project[];
    creationType: string;
    createSlide: (slide: Slide, files: FileList, creationType: string) => void;
};

const SlideEditor = ({ trigger, projects, creationType, createSlide }: SlideEditorProps) => {
    const [modules, setModules] = useState<Module[]>([]);
    const availableProjectIDs = new Map<string, string>(projects.map((value, index) => [value.projectID, value.projectID]));
    const availableTypes = new Map<string, string>([['PanoramaVFTFrame', 'Panorama VFT']]);

    const [isDialogOpen, setDialogOpen] = useState(false);
    const [slide, setSlide] = useState<Slide>({
        projectID: "",
        moduleID: "",
        slideID: "",
        slideType: "",
        modificationTime: new Date()
    });
    const [stagedFiles, setStagedFiles] = useState<FileList | null>(null);
    const { SERVER_ROOT_URL } = useContext(SettingsContext);

    const { setIsToastOpen, setToastMessage, setToastTitle } = useToast();

    const onDrop = (acceptedFiles: File[]) => {
        setStagedFiles(getFileListFromFiles(acceptedFiles, stagedFiles, ['application/zip']));
    }

    const { getRootProps, isDragActive } = useDropzone({ onDrop });

    const onProjectIDChange = (projectId: string) => {
        setSlide({
            ...slide,
            projectID: projectId
        });
    }

    const onModuleIDChange = (moduleId: string) => {
        setSlide({
            ...slide,
            moduleID: moduleId
        });
    }

    const onSlideIDChange = (e: React.FormEvent<HTMLInputElement>) => {
        setSlide({
            ...slide,
            slideID: e.currentTarget.value
        });
    }

    const onSlideTypeChange = (slideType: string) => {
        setSlide({
            ...slide,
            slideType: slideType
        });
    }

    const onFileStaged = (e: React.ChangeEvent<HTMLInputElement>) => {
        setStagedFiles(e.target.files);
    }

    const onStageClear = () => {
        setStagedFiles(null);
    }

    const onDialogClose = () => {
        setDialogOpen(false);
    }

    const isSaveDisabled = () => {
        if (creationType === 'import') {
            return isEmpty(slide.projectID) || isEmpty(slide.moduleID) || (!stagedFiles?.length);
        } else {
            return isEmpty(slide.projectID) || isEmpty(slide.moduleID) || isEmpty(slide.slideID) || isEmpty(slide.slideType) || (!stagedFiles?.length);
        }
    }

    const onSave = () => {
        createSlide(slide, stagedFiles!, creationType);
        setSlide({
            projectID: "",
            moduleID: "",
            slideID: "",
            slideType: "",
            modificationTime: new Date()
        });
        setStagedFiles(null);
        setDialogOpen(false);

        setToastTitle("Submitted");
        setToastMessage("Slide creation requestion submitted.");
        setIsToastOpen(true);
    }

    useEffect(() => {
        (async function () {
            if (!isEmpty(slide.projectID)) {
                try {
                    const response = await fetch(SERVER_ROOT_URL + "/" + slide.projectID + "/modules", {
                        headers: {
                            'Content-Type': 'application/json',
                            'Accept': 'application/json'
                        }
                    });

                    if (response.ok) {
                        setModules(await response.json());
                    } else {
                        setToastTitle("Error");
                        setToastMessage((await response.json()).message);
                        setIsToastOpen(true);
                        setModules([]);
                    }
                } catch {
                    setToastTitle("Error");
                    setToastMessage("Fetch slides unsuccessful. Please try again.")
                    setIsToastOpen(true);
                }
            } else {
                setModules([]);
            }
        })();
    }, [slide?.projectID, SERVER_ROOT_URL, setToastTitle, setToastMessage, setIsToastOpen]);

    const closeBtnCls = 'py-2.5 px-5 text-sm font-medium text-gray-900 focus:outline-none bg-white rounded-lg border border-gray-200 hover:bg-gray-100 hover:text-blue-700 focus:z-10';
    const closeIconCls = 'absolute right-3.5 top-5 inline-flex size-[25px] appearance-none items-center justify-center focus:outline-none text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm w-8 h-8 ms-auto';
    const saveBtnCls = 'py-2.5 px-5 text-white bg-primary-blue hover:bg-blue-800 focus:outline-none font-medium rounded-lg text-sm text-center disabled:bg-gray-200'

    const labelClasses = "block text-left tracking-wide text-gray-700 text-xs font-bold mb-2";
    const inputClasses = "appearance-none block w-full text-gray-700 border border-gray-200 rounded py-2 px-4 leading-tight focus:outline-none focus:bg-white focus:border-gray-500";

    const dialogHeader = creationType === 'import' ? "Import CP6 Module" : "Add new slide";

    return (
        <Dialog.Root open={isDialogOpen} onOpenChange={setDialogOpen}>
            <Dialog.Trigger asChild>
                {trigger}
            </Dialog.Trigger>
            <Dialog.Portal>
                <Dialog.Overlay className="fixed inset-0 bg-blackA6 data-[state=open]:animate-overlayShow" />
                <Dialog.Content onInteractOutside={(e) => { e.preventDefault(); }} className="overflow-y-auto fixed left-1/2 top-1/3 w-[90vw] max-w-[850px] -translate-x-1/2 -translate-y-1/2 rounded-md bg-white p-[25px] shadow-[hsl(206_22%_7%_/_35%)_0px_10px_38px_-10px,_hsl(206_22%_7%_/_20%)_0px_10px_20px_-15px] focus:outline-none data-[state=open]:animate-contentShow">
                    <Dialog.Title className='text-xl font-semibold text-gray-900 my-4'>
                        {dialogHeader}
                    </Dialog.Title>
                    <Dialog.Description />

                    <div className="px-2 my-2 flex flex-wrap">
                        <div className="flex-1 px-3">
                            <div className='my-3 w-full'>
                                {
                                    (stagedFiles && stagedFiles.length > 0)
                                    &&
                                    <div className="w-full py-3 flex justify-between">
                                        <span>Staged file: {Array.from(stagedFiles).map(file => file.name).join(", ")}</span>
                                        <button
                                            type="button"
                                            className="text-white bg-primary-blue hover:bg-blue-800 focus:outline-none font-medium rounded-lg text-sm px-2.5 py-2 text-center"
                                            onClick={onStageClear}>Clear staged file
                                        </button>
                                    </div>
                                }

                                <div className="w-full py-3">
                                    <div {...getRootProps()} className="flex items-center justify-center w-full">
                                        <label className="flex flex-col items-center justify-center w-full h-64 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50">
                                            <div className="flex flex-col items-center justify-center pt-5 pb-6">
                                                <Upload className='mb-2' />
                                                {
                                                    isDragActive
                                                        ? <p className="mb-2 text-sm text-gray-500 dark:text-gray-400"><span className="font-semibold">Drop the files here</span></p>
                                                        : <p className="mb-2 text-sm text-gray-500 dark:text-gray-400"><span className="font-semibold">Click to upload</span> or drag and drop</p>
                                                }
                                                <p className="text-xs text-gray-500 dark:text-gray-400">Exported CP6 module or Adobe animate slide in zip format</p>
                                            </div>
                                            <input type="file" className="hidden" accept=".zip" onChange={onFileStaged} />
                                        </label>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="flex-1 px-3">
                            {creationType === "import"
                                ?
                                <div className='my-3'>
                                    <div className='mb-4'>
                                        <label className={labelClasses}>Project ID</label>
                                        <SelectDropdown
                                            value={slide.projectID}
                                            hasNullValue={true}
                                            options={availableProjectIDs}
                                            onSelectChange={onProjectIDChange} />
                                    </div>
                                    <div className='mb-4'>
                                        <label className={labelClasses}>Module ID</label>
                                        <SelectDropdown
                                            value={slide.moduleID}
                                            hasNullValue={true}
                                            options={new Map<string, string>(modules.map((value, index) => [value.moduleID, value.moduleID]))}
                                            onSelectChange={onModuleIDChange} />
                                    </div>
                                </div>
                                :
                                <div className='my-3'>
                                    <div className='mb-4'>
                                        <label className={labelClasses}>Project ID</label>
                                        <SelectDropdown
                                            value={slide.projectID}
                                            hasNullValue={true}
                                            options={availableProjectIDs}
                                            onSelectChange={onProjectIDChange} />
                                    </div>
                                    <div className='mb-4'>
                                        <label className={labelClasses}>Module ID</label>
                                        <SelectDropdown
                                            value={slide.moduleID}
                                            hasNullValue={true}
                                            options={new Map<string, string>(modules.map((value, index) => [value.moduleID, value.moduleID]))}
                                            onSelectChange={onModuleIDChange} />
                                    </div>
                                    <div className='mb-4'>
                                        <label className={labelClasses}>Slide ID</label>
                                        <input className={inputClasses} value={slide.slideID} onChange={onSlideIDChange} />
                                    </div>
                                    <div className='mb-4'>
                                        <label className={labelClasses}>Type</label>
                                        <SelectDropdown
                                            value={slide.slideType}
                                            hasNullValue={true}
                                            options={availableTypes}
                                            onSelectChange={onSlideTypeChange} />
                                    </div>
                                </div>
                            }
                        </div>
                    </div>

                    <div className="flex items-center justify-between px-2 py-4 md:py-5 border-t border-gray-200 rounded-b">
                        <button type="button" className={closeBtnCls} onClick={onDialogClose}>Close</button>
                        <button type="button" className={saveBtnCls} disabled={isSaveDisabled()} onClick={onSave}>Save</button>
                    </div>

                    <Dialog.Close asChild>
                        <button className={closeIconCls} aria-label="Close"><X /></button>
                    </Dialog.Close>
                </Dialog.Content>
            </Dialog.Portal>
        </Dialog.Root >
    )
}

export default SlideEditor;